import {
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
  StockListResponseType,
} from "@sellar-io/api-v2/types";
import {
  Button,
  SingleFieldDialog,
  SingleFieldDialogHeader,
  SingleFieldDialogContent,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { useState } from "react";
import { DeliveryMethodRadioSelector } from "../../../../Components/DeliveryMethodRadioSelector";
import {
  ContextOrderBasketType,
  useBasketContext,
} from "../../../../../../hooks";
import { capitalize } from "@sellar-io/common";
import { useStorefrontPromotion } from "../../StorefrontPromotion/StorefrontPromotionProvider.shared";
import { CheckoutSelectedFulfillmentType } from "../../../..";
import { useStorefrontBuyerPostcode } from "../../lib/useStorefrontBuyerPostcode";

export const SwitchDeliveryMethodDialog = ({
  active,
  onDismiss,
  supplier,
  supplierStorefront,
  currentFulfillmentType,
  availableDeliveryMethods,
  onFulfillmentTypeChange,
  order,
  productName,
  productItem,
}: {
  active: boolean;
  onDismiss: () => void;
  supplier: SupplierGetResponseType;
  supplierStorefront: SupplierStorefrontV2GetResponseType;
  currentFulfillmentType: CheckoutSelectedFulfillmentType;
  availableDeliveryMethods: SupplierStorefrontV2GetResponseType["deliveryMethods"];
  onFulfillmentTypeChange: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
  order: ContextOrderBasketType | null;
  productName: string;
  productItem: StockListResponseType[number]["Products"][number];
}) => {
  const { buyerPostcode } = useStorefrontBuyerPostcode();

  const initialFulfillmentType = availableDeliveryMethods?.[0]?.fulfillmentType
    ? availableDeliveryMethods[0].fulfillmentType
    : supplier.allowCollectionOnCheckout
      ? "collection"
      : undefined;

  const [selectedFulfillmentType, setSelectedFulfillmentType] =
    useState<CheckoutSelectedFulfillmentType>(initialFulfillmentType);

  const [{ modifyOrderItem }] = useBasketContext();
  const { applyEligiblePromotionRewards, claimedPromotionRewardItemIds } =
    useStorefrontPromotion();

  const filteredStorefront = {
    ...supplierStorefront,
    deliveryMethods: availableDeliveryMethods,
  };

  const handleConfirm = () => {
    if (selectedFulfillmentType) {
      onFulfillmentTypeChange(selectedFulfillmentType);

      modifyOrderItem?.({
        productItem,
        quantity: 1,
        applyEligiblePromotionRewards: ({ order }) => {
          return applyEligiblePromotionRewards({
            order,
            claimedPromotionRewardItemIds,
          });
        },
      });
    }
    onDismiss();
  };

  const currentFulfillmentTypeLabel =
    currentFulfillmentType === "haulier"
      ? "Pallet"
      : capitalize(currentFulfillmentType ?? "");

  return (
    <SingleFieldDialog active={active} onDismiss={onDismiss} size="large">
      <SingleFieldDialogHeader
        title={`This item can't be delivered by ${currentFulfillmentTypeLabel}`}
        description={
          <span className="text-base text-zinc-500">
            To add <span className="font-semibold">{productName}</span> to your
            basket you need to select one of the alternative delivery methods
            listed below
          </span>
        }
        icon="INFORMATION"
        iconClassName="bg-blue-50 text-blue-600"
      />
      <SingleFieldDialogContent className="px-4">
        <DeliveryMethodRadioSelector
          value={selectedFulfillmentType}
          onUpdate={(value) => setSelectedFulfillmentType(value)}
          connection={supplierStorefront.SupplierRetailerConnection}
          order={order}
          supplier={supplier}
          supplierStorefront={filteredStorefront}
          buyerPostcode={buyerPostcode}
        />
      </SingleFieldDialogContent>
      <SingleFieldDialogCTA className="flex flex-col gap-2 space-x-0">
        <Button
          onClick={handleConfirm}
          disabled={!selectedFulfillmentType}
          className="w-full"
        >
          Add item and switch delivery method
        </Button>
        <Button color="secondary" onClick={onDismiss} className="w-full">
          Don't add and keep current delivery method
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};
